import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { EditorContent, useEditorState } from '@tiptap/react';
import type { Editor } from '@tiptap/react';
import React, { useEffect, useRef } from 'react';
import InlineRichTextToolbar from './InlineRichTextToolbar';
import PasteCleanupBanner from './PasteCleanupBanner';
import type { InlineRichTextPreset } from './inlineRichTextPresets';
import { useInlineRichTextEditor } from './useInlineRichTextEditor';

interface InlineRichTextProps {
  html: string;
  onChange: (html: string) => void;
  onLiveChange?: (html: string) => void;
  onFocus?: () => void;
  editorRef?: React.MutableRefObject<Editor | null>;
  onEditorReady?: (editor: Editor) => void;
  focusOnMount?: boolean;
  className?: string;
  placeholder: string;
  /** The stored value is a placeholder / not-yet-set marker (caller decides). */
  unset?: boolean;
  ariaLabel: string;
  /** Prose typography styles, applied to the editable region. */
  sx?: React.CSSProperties;
  /** Which formatting is available; defaults to the CMS Text Section set. */
  preset?: InlineRichTextPreset;
  /** While empty, reserve this many lines of height; shrinks to fit once there's content. */
  minLines?: number;
}

/**
 * The in-app News article's reading styles (fides-capital-native-app
 * src/utils/newsArticleMarkdownStyles.ts), so the article editor shows the post
 * the way app readers will see it: 16/24px body, 14px between paragraphs and
 * after lists, list text indented like the app's bullets, 20/17px headings.
 */
const ARTICLE_READING_SX = {
  '& .ProseMirror': { fontSize: '16px', lineHeight: '24px' },
  '& .ProseMirror p': { margin: '0 0 14px' },
  '& .ProseMirror p + p': { marginTop: 0 },
  '& .ProseMirror > :last-child': { marginBottom: 0 },
  '& .ProseMirror ul, & .ProseMirror ol': { margin: '0 0 14px', paddingLeft: '26px' },
  '& .ProseMirror li': { marginBottom: '6px' },
  '& .ProseMirror li p': { margin: 0 },
  '& .ProseMirror h2': {
    fontSize: '20px',
    lineHeight: '28px',
    fontWeight: 700,
    margin: '12px 0 8px',
  },
  '& .ProseMirror h3': {
    fontSize: '17px',
    lineHeight: '25px',
    fontWeight: 700,
    margin: '10px 0 6px',
  },
};

function proseSx(
  sx?: React.CSSProperties,
  preset?: InlineRichTextPreset,
  emptyMinHeight?: string,
): SxProps<Theme> {
  return {
    '& .ProseMirror': {
      outline: 'none',
      borderRadius: 1,
      px: 0.5,
      mx: -0.5,
      minHeight: emptyMinHeight ?? '1.4em',
      transition: 'background-color 0.12s ease, box-shadow 0.12s ease',
      ...sx,
    },
    '& .ProseMirror:hover': { bgcolor: 'action.hover' },
    '& .ProseMirror-focused': {
      bgcolor: 'transparent',
      boxShadow: (theme: Theme) => `0 0 0 2px ${theme.palette.primary.main}`,
    },
    '& .ProseMirror p': { margin: 0 },
    '& .ProseMirror p + p': { marginTop: '0.5em' },
    '& .ProseMirror ul, & .ProseMirror ol': { margin: '0.25em 0', paddingLeft: '1.25em' },
    '& .ProseMirror h2': { fontSize: '1.05em', fontWeight: 700, margin: '0.4em 0 0.2em' },
    '& .ProseMirror h3': { fontSize: '1em', fontWeight: 700, margin: '0.4em 0 0.2em' },
    ...(preset === 'article' ? ARTICLE_READING_SX : {}),
  };
}

/**
 * Click-to-edit rich text rendered in its real display style. At rest it is the
 * formatted prose (or a dimmed placeholder when empty / unset); on focus a
 * compact toolbar with the allowed formatting appears above it. Commits on blur.
 */
export default function InlineRichText({
  html,
  onChange,
  onLiveChange,
  onFocus,
  editorRef,
  onEditorReady,
  focusOnMount,
  className,
  placeholder,
  unset,
  ariaLabel,
  sx,
  preset,
  minLines,
}: InlineRichTextProps) {
  const { editor, focused, pasteCleanupAvailable, applyPasteCleanup, dismissPasteCleanup } =
    useInlineRichTextEditor({ html, unset, ariaLabel, onChange, onLiveChange, onFocus, preset });
  const onEditorReadyRef = useRef(onEditorReady);
  onEditorReadyRef.current = onEditorReady;

  useEffect(() => {
    if (!editor) return;
    if (editorRef) editorRef.current = editor;
    onEditorReadyRef.current?.(editor);
    if (focusOnMount) editor.commands.focus();
    return () => {
      if (editorRef && editorRef.current === editor) editorRef.current = null;
    };
  }, [editor, editorRef, focusOnMount]);

  // Re-renders whenever emptiness changes — including content loaded in after mount (the editor
  // doesn't re-render on content changes by itself), so the placeholder never covers real text.
  const isEmpty = useEditorState({
    editor,
    selector: ({ editor: current }) => current?.isEmpty ?? true,
  });

  if (!editor) return null;

  const showPlaceholder = !focused && isEmpty;
  const emptyMinHeight =
    isEmpty && minLines ? `${minLines * (Number(sx?.lineHeight) || 1.4)}em` : undefined;

  return (
    <Box className={className} sx={{ position: 'relative' }}>
      {focused && <InlineRichTextToolbar editor={editor} preset={preset} />}
      {pasteCleanupAvailable && (
        <PasteCleanupBanner onFix={applyPasteCleanup} onDismiss={dismissPasteCleanup} />
      )}
      <Box sx={{ position: 'relative' }}>
        {showPlaceholder && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              px: 0.5,
              color: 'text.disabled',
              fontStyle: 'italic',
              pointerEvents: 'none',
              ...sx,
            }}
          >
            {placeholder}
          </Box>
        )}
        <Box sx={proseSx(sx, preset, emptyMinHeight)}>
          <EditorContent editor={editor} />
        </Box>
      </Box>
    </Box>
  );
}
